import React, { useState } from 'react';
import { useQuery } from 'react-query';
import axios from 'axios';
import { motion } from 'framer-motion';
import './ViewerReports.scss';
import { BaseUrl } from '../../../API/ApiHandle';
import { useNavigate } from 'react-router-dom';
import { CheckStringLanguage, toArabicNumbers } from '../../../helpers/ChackLanguage';
import { IoBarChartSharp } from "react-icons/io5";
import Footer from '../../../components/Footer/Footer';
import { toast } from 'react-toastify';
import { ToastOptions } from '../../../helpers/ToastOptions';
import { useTranslation } from 'react-i18next';
import { useCookies } from 'react-cookie';

const ViewerReports = () => {
    const [currentPage, setCurrentPage] = useState(1);
    const navigate = useNavigate();

    const [rqm] = useCookies(["rqm"]);


    const fetchReports = async () => {
        const { data } = await axios.get(`${BaseUrl}/viewer/get_reports?page=${currentPage}`, {
            headers: {
                Authorization: rqm.rqm && `Bearer ${rqm.rqm}`
            }
        });
        return data;
    };

    const {
        data,
        isLoading,
        isError,
        error
    } = useQuery(
        ['reports', currentPage],
        fetchReports,
        {
            keepPreviousData: true,
            staleTime: 5000,
            refetchOnWindowFocus: false,
        }
    );

    const handlePageChange = (newPage) => {
        setCurrentPage(newPage);
    };

    const { t } = useTranslation();

    const MainLanguage = localStorage.getItem("i18nextLng")

    const handleViewReport = (reportId, permission) => {
        if (permission) {
            navigate(`/reportdetails/${reportId}`)
        } else {
            toast.error(t("alerts.Link"), ToastOptions("error"))
        }
    };

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1
            }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                type: "spring",
                damping: 25,
                stiffness: 100
            }
        }
    };

    if (isLoading) {
        return (
            <div className="reports-loader">
                <div className="loader"></div>
                <p>Loading reports...</p>
            </div>
        );
    }

    if (isError) {
        return <div className="reports-error">Error: {error.message}</div>;
    }

    return <>
        <div className="viewer-reports-container container mx-auto">
            <motion.div
                className="reports-list"
                variants={containerVariants}
                initial="hidden"
                animate="visible"
            >
                {data && data.reports && data.reports.length > 0 ? (
                    data.reports.map((report, index) => <motion.div
                        key={index}
                        className={"report-card"}
                        variants={itemVariants}
                        onClick={(e) => {
                            e.stopPropagation(); // prevent triggering the card's onClick
                            handleViewReport(report._id, report?.view_report?.permission);
                        }}
                    >
                        <h2 className={`report-name font-color ${CheckStringLanguage(report.report_name) ? "text-right" : "text-left"}`}>{report.report_name}</h2>

                        <div
                            className="report-image-container"
                            style={{
                                backgroundImage: report?.image
                                    ? `url(${report?.image})`
                                    : "none",
                                backgroundPosition: "center",
                                backgroundSize: "cover",
                                height: "600px",
                                backgroundRepeat: "no-repeat"
                            }}
                        >
                            {report?.image ? null : < IoBarChartSharp className="report-icon w-[40px] h-[40px]" />}

                        </div>

                        <div className={`report-description ${CheckStringLanguage(report.description) ? "text-right" : "text-left"}`}>
                            {report.description || "No description available for this report."}
                        </div>
                        <div className="w-full pt-1">
                            <button className="text-white bg-[#636EFA] hover:bg-[#636efac9] focus:outline-none focus:ring-4 focus:ring-[#636EFA]
                             font-medium rounded-lg text-md w-[150px] py-2  text-center transition cursor-pointer flex justify-center items-center"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleViewReport(report._id, report?.view_report?.permission);
                                }}>{t("reports.SeeMore")}</button>
                        </div>
                    </motion.div>)
                ) : (
                    <motion.div
                        className="no-reports"
                        variants={itemVariants}
                    >
                        <div className="empty-icon">
                            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                                <polyline points="14 2 14 8 20 8"></polyline>
                                <line x1="16" y1="13" x2="8" y2="13"></line>
                                <line x1="16" y1="17" x2="8" y2="17"></line>
                                <polyline points="10 9 9 9 8 9"></polyline>
                            </svg>
                        </div>
                        <h3>No Reports Available</h3>
                        <p>You don't have any reports yet.</p>
                    </motion.div>
                )}
            </motion.div>

            {data && data.total_pages > 1 && (
                <motion.div
                    className="pagination"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                >
                    <button
                        className={`pagination-btn ${currentPage === 1 ? 'disabled' : ''}`}
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                    >
                        {t("Previous")}
                    </button>

                    {[...Array(data.total_pages).keys()].map(page => (
                        <button
                            key={page + 1}
                            className={`pagination-btn ${currentPage === page + 1 ? 'active bg-main' : ''}`}
                            onClick={() => handlePageChange(page + 1)}
                        >
                            {toArabicNumbers(page + 1, MainLanguage)}
                        </button>
                    ))}

                    <button
                        className={`pagination-btn ${currentPage === data.total_pages ? 'disabled' : ''}`}
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === data.total_pages}
                    >
                        {t("Next")}
                    </button>
                </motion.div>
            )}
        </div>

        <Footer />

    </>
};

export default ViewerReports;